import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Select,
  StatCard,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { InvoiceStatus, PaymentModal } from '@/components/ClientLicense';
import { formatDay, formatMoney } from '@/lib/format';
import type { LicenseInvoice } from '@/lib/license';

interface InvoiceList {
  invoices: LicenseInvoice[];
  outstanding: number;
  collected_this_month: number;
  overdue: number;
}

/** Every invoice on the platform, and the one button that matters: paid. */
export default function Invoices() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [paying, setPaying] = useState<LicenseInvoice | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin-invoices', status, kind],
    queryFn: () =>
      api.get<InvoiceList>('/admin/invoices', {
        ...(status ? { status } : {}),
        ...(kind ? { kind } : {}),
      }),
  });

  return (
    <div>
      <PageHeader title="Invoices" description="Licence renewals and usage bills across every client. Record payments as they arrive." />
      {isPending ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={() => refetch()} />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <StatCard label="Outstanding" value={formatMoney(data.outstanding)} tone={data.outstanding > 0 ? 'warn' : 'default'} />
            <StatCard label="Collected this month" value={formatMoney(data.collected_this_month)} tone="good" />
            <StatCard label="Overdue invoices" value={data.overdue} tone={data.overdue ? 'bad' : 'default'} />
          </div>

          <Card
            title="All invoices"
            padded={false}
            actions={
              <>
                <Select value={kind} onChange={(e) => setKind(e.target.value)} className="w-36">
                  <option value="">All types</option>
                  <option value="LICENSE">Licence</option>
                  <option value="USAGE">Usage</option>
                </Select>
                <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-36">
                  <option value="">Any status</option>
                  <option value="OPEN">Open</option>
                  <option value="DRAFT">Draft</option>
                  <option value="PAID">Paid</option>
                  <option value="VOID">Void</option>
                </Select>
              </>
            }
          >
            {data.invoices.length === 0 ? (
              <EmptyState title="No invoices" description="Licence renewals appear here as they are raised." />
            ) : (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Invoice</TH>
                      <TH>Client</TH>
                      <TH>Type</TH>
                      <TH>Period</TH>
                      <TH>Due</TH>
                      <TH>Status</TH>
                      <TH numeric>Total</TH>
                      <TH numeric>Balance</TH>
                      <TH />
                    </TR>
                  </THead>
                  <TBody>
                    {data.invoices.map((invoice) => (
                      <TR key={invoice.id}>
                        <TD className="font-mono text-xs">{invoice.number}</TD>
                        <TD>
                          <Link to={`/clients/${invoice.tenant_id}?tab=license`} className="font-medium text-ink-900 hover:underline dark:text-ink-50">
                            {invoice.client_name}
                          </Link>
                        </TD>
                        <TD>{invoice.kind === 'LICENSE' ? 'Licence' : 'Usage'}</TD>
                        <TD className="whitespace-nowrap text-xs">
                          {formatDay(invoice.period_start)} – {formatDay(invoice.period_end)}
                        </TD>
                        <TD className="whitespace-nowrap text-xs">{formatDay(invoice.due_at)}</TD>
                        <TD>
                          <InvoiceStatus invoice={invoice} />
                        </TD>
                        <TD numeric>{formatMoney(invoice.total, invoice.currency)}</TD>
                        <TD numeric className="font-medium">{formatMoney(invoice.balance, invoice.currency)}</TD>
                        <TD className="text-right">
                          {(invoice.status === 'OPEN' || invoice.status === 'DRAFT') && (
                            <Button size="sm" variant="secondary" onClick={() => setPaying(invoice)}>
                              Record payment
                            </Button>
                          )}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </div>
      )}
      <PaymentModal
        invoice={paying}
        onClose={() => setPaying(null)}
        onDone={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
          queryClient.invalidateQueries({ queryKey: ['licensing'] });
        }}
      />
    </div>
  );
}
